'use client';

import type {
  CreatePrV2CommentInput,
  PrV2CommentResponse,
  PrV2CommentType,
  PrV2DiscussionResponse,
  PrV2ReviewThread,
} from '@/lib/pr-v2/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import type { PrV2ThreadResolveVariables } from './thread-resolution-state';

import {
  deletePrV2Comment,
  editPrV2Comment,
  postPrV2Comment,
  replyToPrV2Comment,
  resolvePrV2CommentThread,
} from './fetch-pr-v2';
import {
  clearPrV2ThreadResolutionOptimistic,
  linkPrV2LocalCommentIds,
  removePrV2LocalCommentsByLookupIds,
  selectPrV2LocalCommentModelView,
  stagePrV2OptimisticComments,
  stagePrV2ThreadResolutionOptimistic,
} from './local-comment-model';
import {
  prV2CommentCreateMutationKey,
  prV2CommentEditMutationKey,
  prV2CommentReplyMutationKey,
  prV2CommentResolveMutationKey,
} from './mutation-keys';
import {
  prV2CodeChangesQueryKeyPrefix,
  prV2CommentsQueryKey,
  prV2CubicReviewSummaryQueryKey,
  prV2DiscussionQueryKey,
  prV2ReviewsQueryKey,
  prV2ReviewThreadsQueryKey,
  prV2SummaryQueryKey,
} from './query-keys';
import {
  addThreadResolveWaiter,
  clearThreadResolveInflight,
  getThreadResolveIntent,
  rejectThreadResolveWaiters,
  removeThreadResolveIntent,
  resetThreadResolveStateForTests,
  resolveSupersededThreadResolveWaiters,
  resolveThreadResolveWaiters,
  setThreadResolveInflight,
  upsertThreadResolveIntent,
} from './thread-resolution-state';

import { trackClientEvent } from '@/lib/analytics/posthog-client';
import {
  pruneStableViewKeys,
  registerStableViewKey,
  transferStableViewKeyToId,
} from '@/lib/pr-v2/stable-view-keys';
import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';
import { usePrStore } from '@/lib/stores/pr-store';
import { useAuth } from '@/providers/auth-provider';

type CommentMutationContext = {
  previousComments?: PrV2CommentResponse[];
  previousDiscussion?: PrV2DiscussionResponse;
  optimisticCommentId?: string;
  previousComment?: PrV2CommentResponse;
  previousTimelineItem?: PrV2DiscussionResponse['timelineItems'][number];
};

type DeleteMutationContext = CommentMutationContext & {
  deletedComment?: PrV2CommentResponse;
  deletedCommentIndex?: number;
  deletedTimelineItem?: PrV2DiscussionResponse['timelineItems'][number];
  deletedTimelineItemIndex?: number;
};

type ResolveMutationContext = {
  previousReviewThreads?: PrV2ReviewThread[];
  previousThread?: PrV2ReviewThread;
  threadNodeId?: string;
};

export type PrV2CommentActionPendingState = {
  addComment: boolean;
  reply: boolean;
  resolve: boolean;
  edit: boolean;
  delete: boolean;
  any: boolean;
};

let optimisticSequence = 0;
const COMMENT_MUTATION_FALLBACK_INVALIDATION_DELAY_MS = 1500;
const fallbackInvalidationTimeoutByPr = new Map<string, ReturnType<typeof setTimeout>>();
const ISSUE_REPLY_PARENT_FROM_FOOTER_REGEX =
  /Replied in \[(?:mrge\.io|cubic\.dev)\]\(https:\/\/(?:www\.)?(?:mrge\.io|cubic\.dev)(?:\/[^)?]*)?\?parent=([^)]+)\)\s*$/i;

function buildOptimisticBaseId(kind: string): string {
  optimisticSequence += 1;
  return `optimistic-${kind}-${Date.now()}-${optimisticSequence}`;
}

/**
 * Transfers the stable view key from the optimistic ID to the real server ID
 * and returns the real ID string, or null if the response lacks a valid ID.
 * After this, both IDs resolve to the same React key, preventing remount.
 */
function transferStableViewKey(
  optimisticId: string | undefined,
  serverResponse: unknown,
): string | null {
  if (!optimisticId) {
    return null;
  }

  // GitHub API returns { id: number, ... } for created comments.
  const response = serverResponse as Record<string, unknown> | null;
  const realId = response?.id;
  if (typeof realId === 'number' && realId > 0) {
    // Keep the optimistic mapping alive: the cache still holds the comment
    // under its optimistic ID until the promotion below swaps it.
    return transferStableViewKeyToId(optimisticId, String(realId));
  }

  return null;
}

function normalizeCommentType(commentType?: PrV2CommentType): PrV2CommentType {
  return commentType === 'issue_comment' ? 'issue_comment' : 'review_comment';
}

/**
 * Issue comment replies are currently sent as issue comments with a cubic footer link.
 * We parse the `parent` query parameter from that footer so the optimistic timeline
 * can keep reply threading consistent before the server response arrives.
 */
function parseIssueReplyParentId(body: string): string | null {
  const match = body.match(ISSUE_REPLY_PARENT_FROM_FOOTER_REGEX);
  return match?.[1] ?? null;
}

function parsePositiveInteger(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed > 0) {
    return parsed;
  }

  return null;
}

function getReviewThreadLookupIds(reviewThread: PrV2ReviewThread | undefined): string[] {
  if (!reviewThread) {
    return [];
  }

  return [reviewThread.id, ...reviewThread.comments.nodes.map((node) => node.id)].filter(
    (lookupId): lookupId is string => typeof lookupId === 'string' && lookupId.length > 0,
  );
}

export function resetPrV2CommentActionStateForTests() {
  for (const timeout of fallbackInvalidationTimeoutByPr.values()) {
    clearTimeout(timeout);
  }
  fallbackInvalidationTimeoutByPr.clear();
  resetThreadResolveStateForTests();
}

/**
 * Creates a client-only comment row so comment lists and thread cards update immediately.
 * Server refetch replaces these optimistic rows with canonical records.
 */
function buildOptimisticComment(args: {
  body: string;
  commentType: PrV2CommentType;
  path?: string;
  line?: number;
  inReplyToId?: string | null;
  githubReviewId?: string | null;
  scopeKey: string;
  userLogin: string;
  userAvatarUrl: string | null;
}): PrV2CommentResponse {
  const optimisticBaseId = buildOptimisticBaseId(args.commentType);
  const timestamp = new Date().toISOString();
  registerStableViewKey(optimisticBaseId, args.scopeKey);
  return {
    id: optimisticBaseId,
    githubCommentId: optimisticBaseId,
    commentType: args.commentType,
    reactionSubjectType: args.commentType,
    githubReviewId: args.githubReviewId ?? null,
    body: args.body,
    path: args.path ?? null,
    line: typeof args.line === 'number' ? args.line : null,
    position: null,
    inReplyToId: args.inReplyToId ?? null,
    userLogin: args.userLogin,
    userAvatarUrl: args.userAvatarUrl,
    createdAtGithub: timestamp,
    updatedAtGithub: timestamp,
    raw: {
      node_id: optimisticBaseId,
      is_optimistic: true,
    },
  };
}

function appendOptimisticComment(
  current: PrV2CommentResponse[] | undefined,
  optimisticComment: PrV2CommentResponse,
): PrV2CommentResponse[] {
  const existing = current ?? [];
  if (existing.some((comment) => comment.id === optimisticComment.id)) {
    return existing;
  }
  return [...existing, optimisticComment];
}

function updateOptimisticCommentBody(
  current: PrV2CommentResponse[] | undefined,
  githubCommentId: string,
  body: string,
): PrV2CommentResponse[] | undefined {
  if (!current) {
    return current;
  }

  const editedAt = new Date().toISOString();
  return current.map((comment) =>
    comment.githubCommentId === githubCommentId
      ? {
          ...comment,
          body,
          updatedAtGithub: editedAt,
        }
      : comment,
  );
}

function removeOptimisticComment(
  current: PrV2CommentResponse[] | undefined,
  githubCommentId: string,
): PrV2CommentResponse[] | undefined {
  if (!current) {
    return current;
  }

  return current.filter((comment) => comment.githubCommentId !== githubCommentId);
}

function restoreDeletedComment(
  current: PrV2CommentResponse[] | undefined,
  deletedComment: PrV2CommentResponse | undefined,
  deletedCommentIndex: number | undefined,
): PrV2CommentResponse[] | undefined {
  if (!deletedComment) {
    return current;
  }

  const existing = current ?? [];
  if (existing.some((comment) => comment.githubCommentId === deletedComment.githubCommentId)) {
    return existing;
  }

  const next = [...existing];
  const insertAt =
    typeof deletedCommentIndex === 'number' && Number.isInteger(deletedCommentIndex)
      ? Math.max(0, Math.min(deletedCommentIndex, next.length))
      : next.length;
  next.splice(insertAt, 0, deletedComment);
  return next;
}

function restoreEditedComment(
  current: PrV2CommentResponse[] | undefined,
  previousComment: PrV2CommentResponse | undefined,
): PrV2CommentResponse[] | undefined {
  if (!current || !previousComment) {
    return current;
  }

  return current.map((comment) =>
    comment.githubCommentId === previousComment.githubCommentId ? previousComment : comment,
  );
}

/**
 * Mirrors optimistic comments into the discussion timeline cache.
 * This keeps timeline and comment-pane UIs in sync while mutations are pending.
 */
function appendOptimisticTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  optimisticComment: PrV2CommentResponse,
  parentId?: string | null,
): PrV2DiscussionResponse | undefined {
  const baseDiscussion: PrV2DiscussionResponse = current ?? {
    syncState: 'ready',
    timelineItems: [],
    commitCheckSuites: [],
    reactions: {},
    cachedAt: null,
  };

  if (baseDiscussion.timelineItems.some((item) => item.id === optimisticComment.githubCommentId)) {
    return baseDiscussion;
  }

  const timelineType =
    optimisticComment.commentType === 'issue_comment'
      ? TIMELINE_ITEM_TYPE.IssueComment
      : TIMELINE_ITEM_TYPE.ReviewComment;
  const parentNumericId = parsePositiveInteger(parentId ?? null);

  const optimisticTimelineItem = {
    id: optimisticComment.githubCommentId,
    type: timelineType,
    timestamp: optimisticComment.createdAtGithub ?? new Date().toISOString(),
    content: optimisticComment.body ?? '',
    node_id:
      typeof optimisticComment.raw === 'object' &&
      optimisticComment.raw &&
      'node_id' in optimisticComment.raw &&
      typeof (optimisticComment.raw as { node_id?: unknown }).node_id === 'string'
        ? (optimisticComment.raw as { node_id: string }).node_id
        : optimisticComment.id,
    user: {
      login: optimisticComment.userLogin ?? 'you',
      avatar_url: optimisticComment.userAvatarUrl ?? '',
      html_url: '',
    },
    ...(parentId ? { parentId } : {}),
    ...(timelineType === TIMELINE_ITEM_TYPE.ReviewComment
      ? {
          in_reply_to_id: parentNumericId,
          path: optimisticComment.path ?? undefined,
          line: optimisticComment.line ?? undefined,
          position: optimisticComment.position ?? undefined,
        }
      : {}),
  } as PrV2DiscussionResponse['timelineItems'][number];

  return {
    ...baseDiscussion,
    timelineItems: [...baseDiscussion.timelineItems, optimisticTimelineItem],
  };
}

function updateOptimisticTimelineItemBody(
  current: PrV2DiscussionResponse | undefined,
  githubCommentId: string,
  body: string,
): PrV2DiscussionResponse | undefined {
  if (!current) {
    return current;
  }

  return {
    ...current,
    timelineItems: current.timelineItems.map((item) =>
      item.id === githubCommentId
        ? {
            ...item,
            content: body,
          }
        : item,
    ),
  };
}

function removeOptimisticTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  githubCommentId: string,
): PrV2DiscussionResponse | undefined {
  if (!current) {
    return current;
  }

  return {
    ...current,
    timelineItems: current.timelineItems.filter((item) => item.id !== githubCommentId),
  };
}

function restoreDeletedTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  deletedTimelineItem: PrV2DiscussionResponse['timelineItems'][number] | undefined,
  deletedTimelineItemIndex: number | undefined,
): PrV2DiscussionResponse | undefined {
  if (!current || !deletedTimelineItem) {
    return current;
  }

  if (current.timelineItems.some((item) => item.id === deletedTimelineItem.id)) {
    return current;
  }

  const nextTimelineItems = [...current.timelineItems];
  const insertAt =
    typeof deletedTimelineItemIndex === 'number' && Number.isInteger(deletedTimelineItemIndex)
      ? Math.max(0, Math.min(deletedTimelineItemIndex, nextTimelineItems.length))
      : nextTimelineItems.length;
  nextTimelineItems.splice(insertAt, 0, deletedTimelineItem);
  return {
    ...current,
    timelineItems: nextTimelineItems,
  };
}

function restoreEditedTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  previousTimelineItem: PrV2DiscussionResponse['timelineItems'][number] | undefined,
): PrV2DiscussionResponse | undefined {
  if (!current || !previousTimelineItem) {
    return current;
  }

  return {
    ...current,
    timelineItems: current.timelineItems.map((item) =>
      item.id === previousTimelineItem.id ? previousTimelineItem : item,
    ),
  };
}

function restoreReviewThread(
  current: PrV2ReviewThread[] | undefined,
  previousThread: PrV2ReviewThread | undefined,
  threadNodeId: string | undefined,
): PrV2ReviewThread[] | undefined {
  if (!current || !previousThread || !threadNodeId) {
    return current;
  }

  return current.map((thread) => (thread.id === threadNodeId ? previousThread : thread));
}

function toResolveError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Failed to update thread resolution');
}

export function usePrV2CommentActions(owner: string, repo: string, number: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { installationId } = usePrStore();
  const prMutationScopeKey = `${owner}/${repo}/${number}`;
  const commentsKey = prV2CommentsQueryKey(owner, repo, number);
  const discussionKey = prV2DiscussionQueryKey(owner, repo, number);
  const reviewThreadsKey = prV2ReviewThreadsQueryKey(owner, repo, number);
  const commentCreateMutationKey = prV2CommentCreateMutationKey(owner, repo, number);
  const commentReplyMutationKey = prV2CommentReplyMutationKey(owner, repo, number);
  const commentEditMutationKey = prV2CommentEditMutationKey(owner, repo, number);
  const commentResolveMutationKey = prV2CommentResolveMutationKey(owner, repo, number);
  const userLogin = user?.user_metadata?.user_name ?? user?.email ?? 'you';
  const userAvatarUrl = user?.user_metadata?.avatar_url ?? null;

  const invalidateAfterMutation = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: prV2SummaryQueryKey(owner, repo, number) }),
      queryClient.invalidateQueries({
        queryKey: prV2CubicReviewSummaryQueryKey(owner, repo, number),
      }),
      queryClient.invalidateQueries({ queryKey: discussionKey }),
      queryClient.invalidateQueries({ queryKey: prV2ReviewsQueryKey(owner, repo, number) }),
      queryClient.invalidateQueries({ queryKey: commentsKey }),
      queryClient.invalidateQueries({ queryKey: reviewThreadsKey }),
      queryClient.invalidateQueries({
        queryKey: prV2CodeChangesQueryKeyPrefix(owner, repo, number),
      }),
    ]);
  };

  const scheduleFallbackInvalidation = () => {
    const existingTimeout = fallbackInvalidationTimeoutByPr.get(prMutationScopeKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      fallbackInvalidationTimeoutByPr.delete(prMutationScopeKey);
      void invalidateAfterMutation();
    }, COMMENT_MUTATION_FALLBACK_INVALIDATION_DELAY_MS);

    fallbackInvalidationTimeoutByPr.set(prMutationScopeKey, timeout);
  };

  const rollbackQueryData = <T>(queryKey: readonly unknown[], previousData: T | undefined) => {
    if (previousData === undefined) {
      queryClient.removeQueries({ queryKey, exact: true });
      return;
    }

    queryClient.setQueryData(queryKey, previousData);
  };

  const pruneStableViewKeysFromCurrentCaches = (extraActiveItemIds?: Iterable<string>) => {
    const activeItemIds = new Set<string>();
    const currentComments = queryClient.getQueryData<PrV2CommentResponse[]>(commentsKey);
    const currentDiscussion = queryClient.getQueryData<PrV2DiscussionResponse>(discussionKey);

    for (const comment of Array.isArray(currentComments) ? currentComments : []) {
      activeItemIds.add(comment.githubCommentId);
      activeItemIds.add(comment.id);
    }

    const currentTimelineItems = Array.isArray(currentDiscussion?.timelineItems)
      ? currentDiscussion.timelineItems
      : [];
    for (const item of currentTimelineItems) {
      if (typeof item.id === 'string' && item.id.length > 0) {
        activeItemIds.add(item.id);
      }
    }

    for (const itemId of extraActiveItemIds ?? []) {
      if (typeof itemId === 'string' && itemId.length > 0) {
        activeItemIds.add(itemId);
      }
    }

    pruneStableViewKeys(prMutationScopeKey, activeItemIds);
  };

  const findCurrentReviewThread = (threadNodeId: string): PrV2ReviewThread | undefined => {
    const cachedReviewThreads = queryClient.getQueryData<PrV2ReviewThread[]>(reviewThreadsKey);
    const cachedThread = cachedReviewThreads?.find((thread) => thread.id === threadNodeId);
    if (cachedThread) {
      return cachedThread;
    }

    return selectPrV2LocalCommentModelView(prMutationScopeKey).reviewThreads.find(
      (thread) => thread.id === threadNodeId,
    );
  };

  const stageQueuedThreadResolveIntent = (threadNodeId: string, resolve: boolean): void => {
    const currentThread = findCurrentReviewThread(threadNodeId);
    if (currentThread) {
      stagePrV2ThreadResolutionOptimistic(prMutationScopeKey, currentThread, resolve);
    }
  };

  useEffect(() => {
    return () => {
      pruneStableViewKeys(prMutationScopeKey, []);
    };
  }, [prMutationScopeKey]);

  /**
   * Maps the real server ID to the same stable view key as the optimistic ID.
   * The optimistic comment stays in the cache with its optimistic markers so
   * the reconciliation safety net can preserve it until the DB catches up.
   * When the server version arrives in a refetch, semantic matching replaces
   * the optimistic row and the shared stable key prevents a React remount.
   */
  const promoteOptimisticComment = (optimisticId: string, serverResponse: unknown) => {
    return transferStableViewKey(optimisticId, serverResponse);
  };

  const addCommentMutation = useMutation({
    mutationKey: commentCreateMutationKey,
    mutationFn: (payload: CreatePrV2CommentInput) => postPrV2Comment(owner, repo, number, payload),
    onMutate: async (payload): Promise<CommentMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: commentsKey }),
        queryClient.cancelQueries({ queryKey: discussionKey }),
      ]);

      const previousComments = queryClient.getQueryData<PrV2CommentResponse[]>(commentsKey);
      const previousDiscussion = queryClient.getQueryData<PrV2DiscussionResponse>(discussionKey);
      const normalizedCommentType = normalizeCommentType(payload.commentType);
      const issueReplyParentId =
        normalizedCommentType === 'issue_comment' ? parseIssueReplyParentId(payload.body) : null;
      const optimisticComment = buildOptimisticComment({
        body: payload.body,
        commentType: normalizedCommentType,
        path: payload.path ?? undefined,
        line: payload.line ?? undefined,
        inReplyToId: issueReplyParentId,
        scopeKey: prMutationScopeKey,
        userLogin,
        userAvatarUrl,
      });

      // Mirror direct-comment optimism into the local model so Discussion/Changes can resolve
      // one stable comment entity even before the query snapshots converge on the server id.
      stagePrV2OptimisticComments(prMutationScopeKey, [optimisticComment]);

      // Update both caches together so discussion and comment panels stay consistent.
      queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
        appendOptimisticComment(current, optimisticComment),
      );
      queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
        appendOptimisticTimelineItem(current, optimisticComment, issueReplyParentId),
      );
      pruneStableViewKeysFromCurrentCaches();

      return {
        previousComments,
        previousDiscussion,
        optimisticCommentId: optimisticComment.githubCommentId,
      };
    },
    onSuccess: (data, variables, context) => {
      if (context?.optimisticCommentId) {
        const promotedCommentId = promoteOptimisticComment(context.optimisticCommentId, data);
        if (promotedCommentId) {
          linkPrV2LocalCommentIds(
            prMutationScopeKey,
            context.optimisticCommentId,
            promotedCommentId,
          );
        }
        pruneStableViewKeysFromCurrentCaches(promotedCommentId ? [promotedCommentId] : undefined);
      }
      trackClientEvent('v2_pr_comment_created', installationId?.toString(), {
        comment_type: variables.commentType ?? 'review_comment',
        owner,
        repo,
        pr_number: number,
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.optimisticCommentId) {
        removePrV2LocalCommentsByLookupIds(prMutationScopeKey, [context.optimisticCommentId]);
        queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
          removeOptimisticComment(current, context.optimisticCommentId as string),
        );
        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          removeOptimisticTimelineItem(current, context.optimisticCommentId as string),
        );
        pruneStableViewKeysFromCurrentCaches();
        return;
      }

      if (context && 'previousComments' in context) {
        rollbackQueryData(commentsKey, context.previousComments);
      }
      if (context && 'previousDiscussion' in context) {
        rollbackQueryData(discussionKey, context.previousDiscussion);
      }
      pruneStableViewKeysFromCurrentCaches();
    },
    onSettled: () => {
      // Keep optimistic rows briefly while mirrored webhook updates and follow-up syncs converge.
      // Fall back to delayed refetch so optimistic rows cannot remain stale indefinitely.
      scheduleFallbackInvalidation();
    },
  });

  const replyMutation = useMutation({
    mutationKey: commentReplyMutationKey,
    mutationFn: ({ commentId, body }: { commentId: number; body: string }) =>
      replyToPrV2Comment(owner, repo, number, commentId, body),
    onMutate: async ({ commentId, body }): Promise<CommentMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: commentsKey }),
        queryClient.cancelQueries({ queryKey: discussionKey }),
      ]);

      const previousComments = queryClient.getQueryData<PrV2CommentResponse[]>(commentsKey);
      const previousDiscussion = queryClient.getQueryData<PrV2DiscussionResponse>(discussionKey);
      const parentComment = (previousComments ?? []).find(
        (comment) => comment.githubCommentId === String(commentId),
      );
      // Reuse parent location metadata so optimistic reply renders in the same thread context.
      const optimisticComment = buildOptimisticComment({
        body,
        commentType: 'review_comment',
        path: parentComment?.path ?? undefined,
        line: parentComment?.line ?? undefined,
        inReplyToId: String(commentId),
        githubReviewId: parentComment?.githubReviewId ?? null,
        scopeKey: prMutationScopeKey,
        userLogin,
        userAvatarUrl,
      });

      stagePrV2OptimisticComments(prMutationScopeKey, [optimisticComment]);

      queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
        appendOptimisticComment(current, optimisticComment),
      );
      queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
        appendOptimisticTimelineItem(current, optimisticComment, String(commentId)),
      );
      pruneStableViewKeysFromCurrentCaches();

      return {
        previousComments,
        previousDiscussion,
        optimisticCommentId: optimisticComment.githubCommentId,
      };
    },
    onSuccess: (data, _variables, context) => {
      if (context?.optimisticCommentId) {
        const promotedCommentId = promoteOptimisticComment(context.optimisticCommentId, data);
        if (promotedCommentId) {
          linkPrV2LocalCommentIds(
            prMutationScopeKey,
            context.optimisticCommentId,
            promotedCommentId,
          );
        }
        pruneStableViewKeysFromCurrentCaches(promotedCommentId ? [promotedCommentId] : undefined);
      }
      trackClientEvent('v2_pr_comment_reply_created', installationId?.toString(), {
        owner,
        repo,
        pr_number: number,
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.optimisticCommentId) {
        removePrV2LocalCommentsByLookupIds(prMutationScopeKey, [context.optimisticCommentId]);
        queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
          removeOptimisticComment(current, context.optimisticCommentId as string),
        );
        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          removeOptimisticTimelineItem(current, context.optimisticCommentId as string),
        );
        pruneStableViewKeysFromCurrentCaches();
        return;
      }

      if (context && 'previousComments' in context) {
        rollbackQueryData(commentsKey, context.previousComments);
      }
      if (context && 'previousDiscussion' in context) {
        rollbackQueryData(discussionKey, context.previousDiscussion);
      }
      pruneStableViewKeysFromCurrentCaches();
    },
    onSettled: () => {
      // Keep optimistic rows briefly while mirrored webhook updates and follow-up syncs converge.
      // Fall back to delayed refetch so optimistic rows cannot remain stale indefinitely.
      scheduleFallbackInvalidation();
    },
  });

  const resolveMutation = useMutation({
    mutationKey: commentResolveMutationKey,
    mutationFn: ({ commentId, resolve, threadNodeId }: PrV2ThreadResolveVariables) =>
      resolvePrV2CommentThread(owner, repo, number, commentId, resolve, threadNodeId),
    onMutate: async ({ threadNodeId, resolve }): Promise<ResolveMutationContext> => {
      // Cancel pending refetches so they cannot overwrite the optimistic state while
      // the mutation is in flight.
      await queryClient.cancelQueries({ queryKey: reviewThreadsKey });

      const previousReviewThreads = queryClient.getQueryData<PrV2ReviewThread[]>(reviewThreadsKey);
      const previousThread = previousReviewThreads?.find((thread) => thread.id === threadNodeId);

      if (previousThread) {
        stagePrV2ThreadResolutionOptimistic(prMutationScopeKey, previousThread, resolve);
      }

      // Optimistically flip `isResolved` in the cache.  PrV2ThreadCard's
      // `localResolvedOverride` gives the instant visual feedback, while
      // this cache update keeps every other consumer (sidebar, file-tree
      // counters, etc.) consistent and prevents stale refetches from
      // flipping the resolved state back before the server confirms.
      if (previousReviewThreads) {
        queryClient.setQueryData<PrV2ReviewThread[]>(reviewThreadsKey, (current) =>
          current?.map((thread) =>
            thread.id === threadNodeId ? { ...thread, isResolved: resolve } : thread,
          ),
        );
      }

      return {
        previousReviewThreads,
        previousThread,
        threadNodeId,
      };
    },
    onSuccess: (_data, variables) => {
      trackClientEvent('v2_pr_comment_resolved', installationId?.toString(), {
        thread_id: variables.threadNodeId,
        resolved: variables.resolve,
        owner,
        repo,
        pr_number: number,
      });
    },
    onError: (_error, _variables, context) => {
      clearPrV2ThreadResolutionOptimistic(
        prMutationScopeKey,
        getReviewThreadLookupIds(context?.previousThread),
      );
      if (context?.previousThread && context.threadNodeId) {
        queryClient.setQueryData<PrV2ReviewThread[]>(reviewThreadsKey, (current) =>
          restoreReviewThread(current, context.previousThread, context.threadNodeId),
        );
      } else if (context && 'previousReviewThreads' in context) {
        rollbackQueryData(reviewThreadsKey, context.previousReviewThreads);
      }
    },
    onSettled: () => {
      // Keep optimistic resolved state stable while mirrored webhook updates and follow-up syncs converge.
      // Fall back to delayed refetch so thread state still self-heals if realtime misses.
      scheduleFallbackInvalidation();
    },
  });

  const flushThreadResolveIntent = async (threadNodeId: string): Promise<void> => {
    const entry = getThreadResolveIntent(prMutationScopeKey, threadNodeId);
    if (!entry || entry.inflightResolved !== null) {
      return;
    }

    const request: PrV2ThreadResolveVariables = {
      ...entry.latestVariables,
      resolve: entry.desiredResolved,
    };

    setThreadResolveInflight(prMutationScopeKey, threadNodeId, request.resolve);

    let shouldSkipFollowup = false;

    try {
      await resolveMutation.mutateAsync(request);
      const latestIntent = getThreadResolveIntent(prMutationScopeKey, threadNodeId);
      if (latestIntent?.desiredResolved === request.resolve) {
        resolveThreadResolveWaiters(prMutationScopeKey, threadNodeId, request.resolve);
      }
    } catch (error) {
      const normalizedError = toResolveError(error);
      const latestIntent = getThreadResolveIntent(prMutationScopeKey, threadNodeId);
      const wasSuperseded =
        latestIntent !== undefined && latestIntent.desiredResolved !== request.resolve;

      if (wasSuperseded) {
        resolveThreadResolveWaiters(prMutationScopeKey, threadNodeId, latestIntent.desiredResolved);
        shouldSkipFollowup = true;
      } else {
        rejectThreadResolveWaiters(
          prMutationScopeKey,
          threadNodeId,
          request.resolve,
          normalizedError,
        );
        toast.error(normalizedError.message);
      }
    } finally {
      clearThreadResolveInflight(prMutationScopeKey, threadNodeId);

      const latestIntent = getThreadResolveIntent(prMutationScopeKey, threadNodeId);
      const shouldDispatchFollowup =
        latestIntent !== undefined &&
        !shouldSkipFollowup &&
        latestIntent.desiredResolved !== request.resolve;

      if (shouldDispatchFollowup) {
        void flushThreadResolveIntent(threadNodeId);
      } else if (latestIntent) {
        removeThreadResolveIntent(prMutationScopeKey, threadNodeId);
      }
    }
  };

  const queueThreadResolveIntent = (variables: PrV2ThreadResolveVariables): Promise<void> => {
    stageQueuedThreadResolveIntent(variables.threadNodeId, variables.resolve);

    const entry = upsertThreadResolveIntent(prMutationScopeKey, variables);
    resolveSupersededThreadResolveWaiters(
      prMutationScopeKey,
      variables.threadNodeId,
      variables.resolve,
    );

    const requestPromise = new Promise<void>((resolve, reject) => {
      addThreadResolveWaiter(prMutationScopeKey, variables.threadNodeId, {
        desiredResolved: variables.resolve,
        resolvePromise: resolve,
        rejectPromise: reject,
      });
    });

    if (entry.inflightResolved === null) {
      void flushThreadResolveIntent(variables.threadNodeId);
    }

    return requestPromise;
  };

  const editMutation = useMutation({
    mutationKey: commentEditMutationKey,
    mutationFn: ({
      commentId,
      body,
      commentType,
    }: {
      commentId: number;
      body: string;
      commentType?: PrV2CommentType;
    }) =>
      editPrV2Comment(owner, repo, number, {
        commentId,
        body,
        commentType,
      }),
    onMutate: async ({ commentId, body }): Promise<CommentMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: commentsKey }),
        queryClient.cancelQueries({ queryKey: discussionKey }),
      ]);

      const previousComments = queryClient.getQueryData<PrV2CommentResponse[]>(commentsKey);
      const previousDiscussion = queryClient.getQueryData<PrV2DiscussionResponse>(discussionKey);
      const githubCommentId = String(commentId);
      const previousComment = previousComments?.find(
        (comment) => comment.githubCommentId === githubCommentId,
      );
      const previousTimelineItem = previousDiscussion?.timelineItems.find(
        (item) => item.id === githubCommentId,
      );

      queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
        updateOptimisticCommentBody(current, githubCommentId, body),
      );
      queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
        updateOptimisticTimelineItemBody(current, githubCommentId, body),
      );

      return {
        previousComments,
        previousDiscussion,
        previousComment,
        previousTimelineItem,
      };
    },
    onSuccess: (_data, variables) => {
      trackClientEvent('v2_pr_comment_edited', installationId?.toString(), {
        comment_id: variables.commentId,
        owner,
        repo,
        pr_number: number,
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousComment) {
        queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
          restoreEditedComment(current, context.previousComment),
        );
      } else if (context && 'previousComments' in context) {
        rollbackQueryData(commentsKey, context.previousComments);
      }
      if (context?.previousTimelineItem) {
        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          restoreEditedTimelineItem(current, context.previousTimelineItem),
        );
      } else if (context && 'previousDiscussion' in context) {
        rollbackQueryData(discussionKey, context.previousDiscussion);
      }
    },
    onSettled: () => {
      // Edit UI is already patched optimistically in both comments/discussion caches.
      // Delay fallback invalidation to reduce projection races while preserving recovery.
      scheduleFallbackInvalidation();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ commentId, commentType }: { commentId: number; commentType: PrV2CommentType }) =>
      deletePrV2Comment(owner, repo, number, commentId, commentType),
    onMutate: async ({ commentId }): Promise<DeleteMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: commentsKey }),
        queryClient.cancelQueries({ queryKey: discussionKey }),
      ]);

      const previousComments = queryClient.getQueryData<PrV2CommentResponse[]>(commentsKey);
      const previousDiscussion = queryClient.getQueryData<PrV2DiscussionResponse>(discussionKey);
      const githubCommentId = String(commentId);
      const deletedCommentIndex = previousComments?.findIndex(
        (comment) => comment.githubCommentId === githubCommentId,
      );
      const deletedComment =
        typeof deletedCommentIndex === 'number' && deletedCommentIndex >= 0
          ? previousComments?.[deletedCommentIndex]
          : undefined;
      const deletedTimelineItemIndex = previousDiscussion?.timelineItems.findIndex(
        (item) => item.id === githubCommentId,
      );
      const deletedTimelineItem =
        typeof deletedTimelineItemIndex === 'number' && deletedTimelineItemIndex >= 0
          ? previousDiscussion?.timelineItems[deletedTimelineItemIndex]
          : undefined;

      queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
        removeOptimisticComment(current, githubCommentId),
      );
      queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
        removeOptimisticTimelineItem(current, githubCommentId),
      );
      pruneStableViewKeysFromCurrentCaches();

      return {
        previousComments,
        previousDiscussion,
        deletedComment,
        deletedCommentIndex:
          typeof deletedCommentIndex === 'number' && deletedCommentIndex >= 0
            ? deletedCommentIndex
            : undefined,
        deletedTimelineItem,
        deletedTimelineItemIndex:
          typeof deletedTimelineItemIndex === 'number' && deletedTimelineItemIndex >= 0
            ? deletedTimelineItemIndex
            : undefined,
      };
    },
    onSuccess: (_data, variables) => {
      trackClientEvent('v2_pr_comment_deleted', installationId?.toString(), {
        comment_id: variables.commentId,
        owner,
        repo,
        pr_number: number,
      });
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      // Restore only the failed delete target so concurrent deletes do not clobber each other.
      if (context.deletedComment) {
        queryClient.setQueryData<PrV2CommentResponse[]>(commentsKey, (current) =>
          restoreDeletedComment(current, context.deletedComment, context.deletedCommentIndex),
        );
      } else if ('previousComments' in context) {
        rollbackQueryData(commentsKey, context.previousComments);
      }

      if (context.deletedTimelineItem) {
        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          restoreDeletedTimelineItem(
            current,
            context.deletedTimelineItem,
            context.deletedTimelineItemIndex,
          ),
        );
      } else if ('previousDiscussion' in context) {
        rollbackQueryData(discussionKey, context.previousDiscussion);
      }
      pruneStableViewKeysFromCurrentCaches();
    },
    onSettled: () => {
      // Delay broad refetch to avoid projection races while users delete multiple comments quickly.
      scheduleFallbackInvalidation();
    },
  });

  const pending: PrV2CommentActionPendingState = {
    addComment: addCommentMutation.isPending,
    reply: replyMutation.isPending,
    resolve: resolveMutation.isPending,
    edit: editMutation.isPending,
    delete: deleteMutation.isPending,
    any:
      addCommentMutation.isPending ||
      replyMutation.isPending ||
      editMutation.isPending ||
      deleteMutation.isPending,
  };

  return {
    addComment: addCommentMutation.mutateAsync,
    replyToComment: replyMutation.mutateAsync,
    resolveCommentThread: queueThreadResolveIntent,
    editComment: editMutation.mutateAsync,
    deleteComment: deleteMutation.mutateAsync,
    pending,
    // Keep legacy boolean for existing call sites while we migrate to scoped flags.
    isMutating: pending.any,
  };
}
